
import React, { useMemo, useState } from "react";



import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  CellClassParams,
  CellStyle,
} from "ag-grid-community";

import {
  Table,
  BarChart2,
  PieChart as PieIcon,
  Donut,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";


import 'ag-grid-community/styles/ag-grid.css';

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { DashboardMRow } from "../../../redux/comparativeDashSlice";
import ExportButton from "../pages/Exportbutton";

interface ComparativeTableProps {
  heading: any;
  rowData: DashboardMRow[];
  dataY?: { LYAmount: number; CYAmount: number; GrowthPer: number };
  yr1Label?: string;
  yr2Label?: string;
}

// ======================================================


// ======================================================
// TYPES
// ======================================================

type FilterMode = "all" | "monthly" | "quarterly";
type ViewMode = "table" | "bar" | "pie" | "donut";

// ======================================================
// COLORS
// ======================================================

const PIE_COLORS = [
  "#1e40af", "#2563eb", "#3b82f6", "#60a5fa",
  "#0f766e", "#14b8a6", "#10b981", "#22c55e",
  "#eab308", "#f97316", "#ef4444", "#8b5cf6",
];

// ======================================================
// FORMAT HELPERS
// ======================================================

const formatAmount = (val: number) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatGrowth = (val: number) =>
  `${Number(val || 0).toFixed(2)}%`;

// ======================================================
// CUSTOM TOOLTIP FOR CHARTS
// ======================================================

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
        fontSize: "13px",
      }}
    >
      <p
        style={{
          margin: "0 0 6px",
          fontWeight: 700,
          color: "#0f172a",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "4px",
        }}
      >
        {label}
      </p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ margin: "3px 0", color: p.color }}>
          <span style={{ fontWeight: 600 }}>{p.name}: </span>
          {formatAmount(p.value)}
        </p>
      ))}
    </div>
  );
};

// ======================================================
// MOBILE CARD COMPONENT
// ======================================================

interface MobileCardProps {
  row: DashboardMRow;
  yr1Label: string;
  yr2Label: string;
}

const MobileCard: React.FC<MobileCardProps> = ({ row, yr1Label, yr2Label }) => {
  const isQuarter = row.UBold === 1;
  const growth = Number(row.GrowthPer || 0);

  const growthColor =
    growth > 0 ? "#059669" : growth < 0 ? "#dc2626" : "#94a3b8";
  const growthBg =
    growth > 0 ? "#dcfce7" : growth < 0 ? "#fee2e2" : "#f1f5f9";

  const GrowthIcon =
    growth > 0 ? TrendingUp : growth < 0 ? TrendingDown : Minus;

  return (
    <div
      style={{
        background: isQuarter ? "#f0f4ff" : "#ffffff",
        border: `1.5px solid ${isQuarter ? "#a5b4fc" : "#e8edf2"}`,
        borderLeft: `4px solid ${isQuarter ? "#0f4c81" : "#cbd5e1"}`,
        borderRadius: "10px",
        padding: "11px 12px",
        marginBottom: "8px",
        boxShadow: isQuarter
          ? "0 3px 10px rgba(99,102,241,0.12)"
          : "0 1px 4px rgba(15,23,42,0.05)",
      }}
    >
      {/* Row Label */}
      <div
        style={{
          fontSize: isQuarter ? "13px" : "12.5px",
          fontWeight: isQuarter ? 700 : 600,
          color: isQuarter ? "#1e3a8a" : "#334155",
          marginBottom: "10px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {isQuarter && (
          <span
            style={{
              background: "#0f4c81",
              color: "#fff",
              fontSize: "9px",
              padding: "2px 6px",
              borderRadius: "4px",
              letterSpacing: "0.5px",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            Q
          </span>
        )}
        {row.Particulars}
      </div>

      {/* Values — vertical rows, label left / value right */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {/* FY1 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(15,76,129,0.05)",
            borderRadius: "7px",
            padding: "7px 10px",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "#64748b",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            FY {yr1Label}
          </span>
          <span
            style={{
              fontSize: "12.5px",
              fontWeight: isQuarter ? 700 : 600,
              color: "#0f172a",
              textAlign: "right",
            }}
          >
            {formatAmount(row.LYAmount || 0)}
          </span>
        </div>

        {/* FY2 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(5,150,105,0.05)",
            borderRadius: "7px",
            padding: "7px 10px",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "#64748b",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            FY {yr2Label}
          </span>
          <span
            style={{
              fontSize: "12.5px",
              fontWeight: isQuarter ? 700 : 600,
              color: "#0f172a",
              textAlign: "right",
            }}
          >
            {formatAmount(row.CYAmount || 0)}
          </span>
        </div>

        {/* Growth */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: growthBg,
            borderRadius: "7px",
            padding: "7px 10px",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "#64748b",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            Growth
          </span>
          <span
            style={{
              fontSize: "12.5px",
              fontWeight: 700,
              color: growthColor,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <GrowthIcon size={13} />
            {formatGrowth(growth)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// MOBILE CARD VIEW
// ======================================================

interface MobileCardViewProps {
  filtered: DashboardMRow[];
  yr1Label: string;
  yr2Label: string;
  grandTotal: { lyTotal: number; cyTotal: number; growthPer: number };
}

const MobileCardView: React.FC<MobileCardViewProps> = ({
  filtered,
  yr1Label,
  yr2Label,
  grandTotal,
}) => {
  const growthColor =
    grandTotal.growthPer > 0
      ? "#86efac"
      : grandTotal.growthPer < 0
      ? "#fca5a5"
      : "#cbd5e1";

  return (
    <div style={{ padding: "12px" }}>
      {/* Cards */}
      <div style={{ marginBottom: "4px" }}>
        {filtered.map((row, idx) => (
          <MobileCard
            key={idx}
            row={row}
            yr1Label={yr1Label}
            yr2Label={yr2Label}
          />
        ))}
      </div>

      {/* Grand Total Bar */}
      <div
        style={{
          background: "#0f4c81",
          borderRadius: "10px",
          padding: "14px 16px",
          marginTop: "4px",
        }}
      >
        <div
          style={{
            color: "#fff",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.5px",
            marginBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <span
            style={{
              background: "rgba(255,255,255,0.2)",
              borderRadius: "4px",
              padding: "2px 6px",
              fontSize: "9px",
            }}
          >
            TOTAL
          </span>
          Grand Total
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "8px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "9px",
                color: "rgba(255,255,255,0.6)",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              FY {yr1Label}
            </div>
            <div
              style={{ color: "#fff", fontWeight: 700, fontSize: "12px" }}
            >
              {formatAmount(grandTotal.lyTotal)}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: "9px",
                color: "rgba(255,255,255,0.6)",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              FY {yr2Label}
            </div>
            <div
              style={{ color: "#fff", fontWeight: 700, fontSize: "12px" }}
            >
              {formatAmount(grandTotal.cyTotal)}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: "9px",
                color: "rgba(255,255,255,0.6)",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              Growth
            </div>
            <div
              style={{
                color: growthColor,
                fontWeight: 700,
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              {grandTotal.growthPer > 0 ? "▲" : grandTotal.growthPer < 0 ? "▼" : ""}
              {Math.abs(grandTotal.growthPer).toFixed(2)}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// MAIN COMPONENT
// ======================================================

const ComparativeTable: React.FC<ComparativeTableProps> = ({
  heading,
  rowData,
  dataY,
  yr1Label: yr1Prop,
  yr2Label: yr2Prop,
}) => {
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("table");



  const yr1Label = yr1Prop || "";
  const yr2Label = yr2Prop || "";

  const filtered = useMemo(() => {
    if (!Array.isArray(rowData)) return [];
    if (filterMode === "monthly") return rowData.filter((r) => r.UBold === 0);
    if (filterMode === "quarterly") return rowData.filter((r) => r.UBold === 1);
    return rowData;
  }, [rowData, filterMode]);

  const chartData = useMemo(() => {
    if (!Array.isArray(rowData)) return [];
    return rowData.filter((r) => r.UBold === 0);
  }, [rowData]);

  const grandTotal = useMemo(() => {
    if (dataY) {
      return {
        lyTotal: dataY.LYAmount,
        cyTotal: dataY.CYAmount,
        growthPer: dataY.GrowthPer,
      };
    }
    const monthRows = (Array.isArray(rowData) ? rowData : []).filter((r) => r.UBold === 0);
    const lyTotal = monthRows.reduce((sum, r) => sum + (Number(r.LYAmount) || 0), 0);
    const cyTotal = monthRows.reduce((sum, r) => sum + (Number(r.CYAmount) || 0), 0);
    const growthPer = lyTotal > 0 ? ((cyTotal - lyTotal) / lyTotal) * 100 : 0;
    return { lyTotal, cyTotal, growthPer };
  }, [rowData, dataY]);




  const columnDefs: ColDef<DashboardMRow>[] = useMemo(
    () => [
      {
        field: "Particulars",
        headerName: "Particulars",
        minWidth: 180,
        flex: 2,
        cellStyle: (p: CellClassParams<DashboardMRow, any>): CellStyle => {
          const isQuarter = p.data?.UBold === 1;
          return {
            fontWeight: isQuarter ? "700" : "500",
            color: isQuarter ? "#0f172a" : "#374151",
            backgroundColor: isQuarter ? "#eef2ff" : "transparent",
            display: "flex",
            alignItems: "center",
            paddingLeft: isQuarter ? "12px" : "20px",
            fontSize: isQuarter ? "13.5px" : "12px",
            letterSpacing: isQuarter ? "0.3px" : "normal",
          };
        },
      },
      {
        field: "LYAmount",
        headerName: `FY ${yr1Label}`,
        type: "rightAligned",
        flex: 1.5,
        minWidth: 140,
        valueFormatter: (p) => formatAmount(p.value),
        cellStyle: (p: CellClassParams<DashboardMRow, any>): CellStyle => ({
          fontWeight: p.data?.UBold === 1 ? "700" : "500",
          backgroundColor: p.data?.UBold === 1 ? "#eef2ff" : "transparent",
          color: "#1e293b",
          textAlign: "right",
          paddingRight: "16px",
        }),
      },
      {
        field: "CYAmount",
        headerName: `FY ${yr2Label}`,
        type: "rightAligned",
        flex: 1.5,
        minWidth: 140,
        valueFormatter: (p) => formatAmount(p.value),
        cellStyle: (p: CellClassParams<DashboardMRow, any>): CellStyle => ({
          fontWeight: p.data?.UBold === 1 ? "700" : "500",
          backgroundColor: p.data?.UBold === 1 ? "#eef2ff" : "transparent",
          color: "#1e293b",
          textAlign: "right",
          paddingRight: "16px",
        }),
      },
      {
        field: "GrowthPer",
        headerName: "Growth %",
        type: "rightAligned",
        flex: 1,
        minWidth: 110,
        valueFormatter: (p) => formatGrowth(p.value),
        cellStyle: (p: CellClassParams<DashboardMRow, any>): CellStyle => {
          const val = Number(p.data?.GrowthPer || 0);
          return {
            fontWeight: "700",
            backgroundColor: p.data?.UBold === 1 ? "#eef2ff" : "transparent",
            color: val > 0 ? "#059669" : val < 0 ? "#dc2626" : "#94a3b8",
            textAlign: "right",
            paddingRight: "16px",
            fontSize: "13px",
          };
        },
      },
    ],
    [yr1Label, yr2Label]
  );

  const gridStyle = useMemo(
    () =>
      ({
        "--ag-header-background-color": "#0f4c81",
        "--ag-header-foreground-color": "#ffffff",
        "--ag-header-column-separator-color": "rgba(255,255,255,0.2)",
        "--ag-header-column-resize-handle-color": "rgba(255,255,255,0.4)",
        "--ag-background-color": "#ffffff",
        "--ag-odd-row-background-color": "#f8fafc",
        "--ag-row-hover-color": "#dbeafe",
        "--ag-selected-row-background-color": "#bfdbfe",
        "--ag-border-color": "#e2e8f0",
        "--ag-cell-horizontal-border": "solid #f1f5f9",
        "--ag-row-border-color": "#e8edf2",
        "--ag-font-size": "13px",
        "--ag-font-family": "'Segoe UI', system-ui, sans-serif",
        "--ag-header-height": "46px",
        "--ag-row-height": "40px",
        "--ag-cell-horizontal-padding": "12px",
        // minHeight:'150px',
        height: "470px",
        width: "100%",
        borderRadius: "0 0 12px 12px",
        overflow: "hidden",
      } as React.CSSProperties),
    []
  );

  const renderChartContent = () => {
    if (viewMode === "bar") {
      return (
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
        >
          <defs>
            <linearGradient id="gradLY" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.5} />
            </linearGradient>
            <linearGradient id="gradCY" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#059669" stopOpacity={0.5} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey="Particulars"
            angle={-40}
            textAnchor="end"
            interval={0}
            height={75}
            tick={{ fontSize: 11, fill: "#64748b" }}
          />
          <YAxis
            tickFormatter={(v) =>
              v >= 10000000
                ? `${(v / 10000000).toFixed(1)}Cr`
                : v >= 100000
                ? `${(v / 100000).toFixed(1)}L`
                : v.toLocaleString("en-IN")
            }
            tick={{ fontSize: 11, fill: "#64748b" }}
          />
          <Tooltip content={<CustomTooltip yr1Label={yr1Label} yr2Label={yr2Label} />} />
          <Legend wrapperStyle={{ paddingTop: "12px", fontSize: "12px" }} />
          <Bar dataKey="LYAmount" name={`FY ${yr1Label}`} fill="url(#gradLY)" radius={[5, 5, 0, 0]} barSize={20} />
          <Bar dataKey="CYAmount" name={`FY ${yr2Label}`} fill="url(#gradCY)" radius={[5, 5, 0, 0]} barSize={20} />
        </BarChart>
      );
    }

    if (viewMode === "pie" || viewMode === "donut") {
      return (
        <PieChart>
          <Pie
            data={chartData}
            dataKey="LYAmount"
            nameKey="Particulars"
            cx="28%"
            cy="50%"
            innerRadius={viewMode === "donut" ? 55 : 0}
            outerRadius={110}
            paddingAngle={2}
            label={({ percent }: { percent?: number }) =>
              (percent ?? 0) > 0.04 ? `${((percent ?? 0) * 100).toFixed(0)}%` : ""
            }
            labelLine={false}
          >
            {chartData.map((_, index) => (
              <Cell key={`ly-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} opacity={0.85} />
            ))}
          </Pie>
          <Pie
            data={chartData}
            dataKey="CYAmount"
            nameKey="Particulars"
            cx="72%"
            cy="50%"
            innerRadius={viewMode === "donut" ? 55 : 0}
            outerRadius={110}
            paddingAngle={2}
            label={({ percent }: { percent?: number }) =>
              (percent ?? 0) > 0.04 ? `${((percent ?? 0) * 100).toFixed(0)}%` : ""
            }
            labelLine={false}
          >
            {chartData.map((_, index) => (
              <Cell key={`cy-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number | string | any) => formatAmount(Number(value))} />
          <Legend />
          <text x="28%" y="94%" textAnchor="middle" fill="#64748b" fontSize={12} fontWeight={600}>FY {yr1Label}</text>
          <text x="72%" y="94%" textAnchor="middle" fill="#64748b" fontSize={12} fontWeight={600}>FY {yr2Label}</text>
        </PieChart>
      );
    }

    return null;
  };

  return (
    <>
      <style>{`
        /* AG Grid overrides */
        .comp-grid .ag-header,
        .comp-grid .ag-header-row,
        .comp-grid .ag-header-cell {
          background-color: #0f4c81 !important;
          color: #ffffff !important;
        }
        .comp-grid .ag-header-cell-text {
          color: #ffffff !important;
          font-weight: 700 !important;
        }
        .comp-grid .ag-icon {
          color: rgba(255,255,255,0.7) !important;
        }
        .comp-grid .ag-row:hover {
          background-color: #dbeafe !important;
          transition: background-color 0.15s ease;
        }
        .comp-grid .ag-header-cell {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.4px;
        }
        .comp-grid .ag-header-cell-label {
          justify-content: flex-end;
        }
        .comp-grid .ag-header-cell:first-child .ag-header-cell-label {
          justify-content: flex-start;
        }
        .comp-grid .ag-paging-panel {
          font-size: 12px;
          color: #475569;
          border-top: 1px solid #e2e8f0;
          padding: 8px 14px;
        }
        .comp-grid .ag-root-wrapper {
          border: none !important;
        }

        /* ============================================
           RESPONSIVE TOOLBAR
        ============================================ */
        .comp-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 13px 16px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          flex-wrap: wrap;
          gap: 8px;
        }

        .comp-toolbar-left {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          min-width: 0;
        }

        .comp-toolbar-heading {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          white-space: nowrap;
        }

        .comp-filter-group {
          display: flex;
          background: #e8edf4;
          padding: 3px;
          border-radius: 9px;
          gap: 2px;
          flex-shrink: 0;
        }

        .comp-filter-btn {
          padding: 5px 12px;
          border: none;
          border-radius: 7px;
          cursor: pointer;
          font-size: 11px;
          letter-spacing: 0.5px;
          transition: all 0.15s ease;
        }

        .comp-view-group {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }

        .comp-view-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        /* Mobile: stack heading on its own line, filters + icons on next */
        @media (max-width: 575px) {
          .comp-toolbar {
            flex-direction: column;
            align-items: flex-start;
            padding: 10px 12px;
            gap: 8px;
          }

          .comp-toolbar-heading {
            font-size: 14px;
            width: 100%;
          }

          .comp-toolbar-controls {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            gap: 8px;
          }

          .comp-filter-btn {
            padding: 5px 9px;
            font-size: 10px;
          }

          .comp-view-btn {
            width: 30px;
            height: 30px;
          }
        }

        /* ============================================
           MOBILE CARD VIEW — only on small screens
        ============================================ */
        .comp-mobile-view {
          display: none;
        }
        .comp-desktop-view {
          display: block;
        }

        @media (max-width: 575px) {
          .comp-mobile-view {
            display: block;
          }
          .comp-desktop-view {
            display: none;
          }
        }
      `}</style>

      <div
        style={{
          background: "#ffffff",
          borderRadius: "14px",
          border: "1px solid #dbe3ef",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(15,23,42,0.09)",
        }}
      >
        {/* ------------------------------------------------ */}
        {/* RESPONSIVE TOOLBAR                               */}
        {/* ------------------------------------------------ */}
        <div className="comp-toolbar">
          {/* Heading — always full width on mobile */}
          <h3 className="comp-toolbar-heading">
            {heading} Comparison Details
          </h3>
          {/* Controls row: filters + view icons */}
          <div className="comp-toolbar-controls" style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {/* FILTER BUTTONS */}
            <div className="comp-filter-group">
              {(["all", "monthly", "quarterly"] as FilterMode[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilterMode(f)}
                  className="comp-filter-btn"
                  style={{
                    fontWeight: filterMode === f ? 700 : 500,
                    background: filterMode === f ? "#0f4c81" : "transparent",
                    color: filterMode === f ? "#ffffff" : "#64748b",
                  }}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>

            {/* VIEW MODE BUTTONS */}
            <div className="comp-view-group">
              <span>
              <ExportButton variant="light" params={{ type:"comparative", heading, rows:filtered, yr1Label, yr2Label, filterMode, grandTotal }} />
              </span>
              {[
                { v: "table", icon: <Table size={15} /> },
                { v: "pie", icon: <PieIcon size={15} /> },
                { v: "donut", icon: <Donut size={15} /> },
                { v: "bar", icon: <BarChart2 size={15} /> },
              ].map(({ v, icon }) => (
                <button
                  key={v}
                  onClick={() => setViewMode(v as ViewMode)}
                  title={v.toUpperCase()}
                  className="comp-view-btn"
                  style={{
                    borderColor: viewMode === v ? "#0f4c81" : "#dbe3ef",
                    background: viewMode === v ? "#0f4c81" : "#ffffff",
                    color: viewMode === v ? "#ffffff" : "#64748b",
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ------------------------------------------------ */}
        {/* TABLE VIEW — Desktop only                        */}
        {/* ------------------------------------------------ */}
        {viewMode === "table" ? (
          <>
            {/* Desktop Table */}
            <div className="comp-desktop-view">
              <div className=" comp-grid" style={gridStyle}>
  
             <AgGridReact
                     theme="legacy"
                  rowData={filtered}
                  columnDefs={columnDefs}
                  headerHeight={46}
                  rowHeight={36}
                  animateRows={true}
                  pagination={false}
                  paginationPageSize={20}
                  suppressMovableColumns={true}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                  }}
                  getRowStyle={(params:any) => {
                    if (params.data?.UBold === 1) {
                      return {
                        fontWeight: "700",
                        background: "#eef2ff",
                        borderBottom: "1px solid #c7d2fe",
                      };
                    }
                    return undefined;
                  }}
                />
              </div>

              {/* Desktop Grand Total Bar */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1.5fr 1fr",
                  background: "#0f4c81",
                  padding: "12px 16px",
                  borderTop: "2px solid #0d3d68",
                }}
              >
                <div
                  style={{
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    paddingLeft: "12px",
                  }}
                >
                  <span
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      fontSize: "10px",
                      letterSpacing: "0.5px",
                    }}
                  >
                 Grand Total
                  </span>
                  {/* Grand Total */}
                </div>
                <div style={{ color: "#ffffff", fontWeight: 700, fontSize: "13px", textAlign: "right", paddingRight: "16px" }}>
                  {formatAmount(grandTotal.lyTotal)}
                </div>
                <div style={{ color: "#ffffff", fontWeight: 700, fontSize: "13px", textAlign: "right", paddingRight: "16px" }}>
                  {formatAmount(grandTotal.cyTotal)}
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "13px",
                    textAlign: "right",
                    paddingRight: "16px",
                    color:
                      grandTotal.growthPer > 0 ? "#86efac" : grandTotal.growthPer < 0 ? "#fca5a5" : "#cbd5e1",
                  }}
                >
                  {grandTotal.growthPer > 0 ? "▲" : grandTotal.growthPer < 0 ? "▼" : ""}{" "}
                  {Math.abs(grandTotal.growthPer).toFixed(2)}%
                </div>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="comp-mobile-view">
              <MobileCardView
                filtered={filtered}
                yr1Label={yr1Label}
                yr2Label={yr2Label}
                grandTotal={grandTotal}
              />
            </div>
          </>
        ) : null}




        {/* ------------------------------------------------ */}
        {/* CHART VIEW                                       */}
        {/* ------------------------------------------------ */}
        {viewMode !== "table" && (
          <div style={{ height: "480px", padding: "20px 16px 16px" }}>
            {chartData.length === 0 ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}
              >
                No data available for chart
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {renderChartContent()!}
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ComparativeTable;